import { useEffect } from "react";
import i18n, { useT } from "@/lib/i18n";
import { onboardingHref } from "@/lib/onboardingUrl";

/**
 * Shared bits for config-driven landing templates.
 *
 * A landing variant row (landing_variants.config, created via the ops MCP)
 * may set `template` to pick a layout other than the classic LandingDynamic.
 * Each template component takes the full variant config as its only prop and
 * treats it as fully-resolved content (plain strings, no i18n keys) — MCP
 * variants are authored per-language. See docs/landing-templates.md.
 */

/**
 * Every template's CTA target.
 *
 * Delegates to the canonical `onboardingHref` helper rather than building a
 * `/quiz` URL by hand: onboarding is the real entry point, and centralising it
 * means templates pick up the localhost/subdomain split and any future param
 * for free. Extra params (e.g. the longform band's `city`) ride along in
 * `extra`; empty values are dropped by the helper.
 */
export function quizHref(
  audienceParam: string,
  eventTypeParam?: string,
  extra: Record<string, string | null | undefined> = {},
): string {
  return onboardingHref({ a: audienceParam, et: eventTypeParam, ...extra });
}

/** Aggregate member count. A figure, not copy, so it lives here. */
const MEMBER_COUNT = 60000;

/**
 * Social-proof strip: real aggregate numbers, never invented quotes.
 *
 * The rating and member count are figures, so they stay in code; the word
 * "members" is copy, so it comes from the locale files and follows the
 * variant's language like the rest of the chrome.
 */
export function ProofStrip({ className = "" }: { className?: string }) {
  const { t, language } = useT("landing");
  return (
    <div className={`flex items-center justify-center gap-1.5 text-sm ${className}`}>
      <span className="text-amber-600 dark:text-amber-400 tracking-tight" aria-hidden>
        {"\u2605".repeat(5)}
      </span>
      <span className="font-semibold">4.8</span>
      <span className="opacity-50" aria-hidden>
        &middot;
      </span>
      {/* Not `count`: that name makes i18next resolve a plural form
          (members_other) and the key would silently miss. The separator is
          locale-dependent too, so the number is formatted, not stringified. */}
      <span>
        {t("proofStrip.members", { members: MEMBER_COUNT.toLocaleString(language) })}
      </span>
    </div>
  );
}

/** Sticky bottom CTA used by several templates. */
export function StickyCta({ href, label }: { href: string; label: string }) {
  return (
    <div className="fixed bottom-0 inset-x-0 md:hidden z-50 bg-gradient-to-t from-neutral-200 dark:from-neutral-900 via-neutral-200/95 dark:via-neutral-900/95 to-transparent pt-6 pb-4 px-4">
      <a
        href={href}
        className="rounded-full bg-primary-700 hover:bg-primary-800 text-white font-bold text-base py-3 w-full inline-flex items-center justify-center transition-colors shadow-lg"
      >
        {label}
      </a>
    </div>
  );
}

/**
 * Pins i18n to the variant's language for as long as the page is mounted.
 *
 * A variant's `config` carries fully-resolved copy, but the chrome a template
 * still resolves through `useT` (footer, the longform band's labels, date
 * formatting) does not live in config. Without this, a `language: "de"`
 * variant renders German body copy under English chrome whenever the
 * visitor's browser is not German — which, for paid traffic landing on a
 * German page, is most of them. Mirrors the effect LandingDynamic has always
 * run; TemplatePage calls it for every template so they cannot drift.
 */
export function useVariantLanguage(language?: string) {
  useEffect(() => {
    if (!language || language === "en") return;
    i18n.changeLanguage(language);
    return () => {
      i18n.changeLanguage("en");
    };
  }, [language]);
}
